
import { getAppCheckHeader, getAuthHeader } from './firebase';

/**
 * Trims transparent pixels from the edges of a canvas and resizes if too large.
 */
function trimAndResizeCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let minX = width, minY = height, maxX = 0, maxY = 0;
    let found = false;

    // Scan for non-transparent pixels
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const alpha = data[(y * width + x) * 4 + 3];
            if (alpha > 0) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
                found = true;
            }
        }
    }

    if (!found) return canvas;

    const padding = 2;
    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = Math.min(width, maxX + padding);
    maxY = Math.min(height, maxY + padding);

    const trimmedWidth = maxX - minX;
    const trimmedHeight = maxY - minY;

    const MAX_SIZE = 512;
    let targetWidth = trimmedWidth;
    let targetHeight = trimmedHeight;

    if (trimmedWidth > MAX_SIZE || trimmedHeight > MAX_SIZE) {
        const ratio = Math.min(MAX_SIZE / trimmedWidth, MAX_SIZE / trimmedHeight);
        targetWidth = Math.round(trimmedWidth * ratio);
        targetHeight = Math.round(trimmedHeight * ratio);
    }

    const trimmedCanvas = document.createElement('canvas');
    trimmedCanvas.width = targetWidth;
    trimmedCanvas.height = targetHeight;
    const trimmedCtx = trimmedCanvas.getContext('2d');
    
    if (trimmedCtx) {
        trimmedCtx.drawImage(
            canvas, 
            minX, minY, trimmedWidth, trimmedHeight, 
            0, 0, targetWidth, targetHeight
        );
    }

    return trimmedCanvas;
}

export function processImageWithTransparency(base64Data: string, mode: 'auto' | 'white' = 'auto'): Promise<string> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
        resolve(base64Data);
        return;
    }

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(base64Data);
        return;
      }
      
      ctx.drawImage(img, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const width = canvas.width;
      const height = canvas.height;

      let bgR, bgG, bgB;

      if (mode === 'white') {
          bgR = 255;
          bgG = 255;
          bgB = 255;
      } else {
          bgR = data[0];
          bgG = data[1];
          bgB = data[2];
      }

      const tolerance = 50; 

      const isBackground = (idx: number) => {
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        const a = data[idx + 3];
        
        if (a === 0) return false; 

        return Math.abs(r - bgR) < tolerance &&
               Math.abs(g - bgG) < tolerance &&
               Math.abs(b - bgB) < tolerance;
      };

      if (mode === 'white') {
          for (let i = 0; i < data.length; i += 4) {
              if (isBackground(i)) {
                  data[i + 3] = 0;
              }
          }
      } else {
          const stack = [
              [0, 0], 
              [width - 1, 0], 
              [0, height - 1], 
              [width - 1, height - 1]
          ];
          
          const seen = new Uint8Array(width * height);
          
          const getIdx = (x: number, y: number) => (y * width + x) * 4;
          const getSeenIdx = (x: number, y: number) => y * width + x;

          while (stack.length > 0) {
            const [x, y] = stack.pop()!;
            const seenIdx = getSeenIdx(x, y);

            if (seen[seenIdx]) continue;
            seen[seenIdx] = 1;

            const idx = getIdx(x, y);

            if (isBackground(idx)) {
              data[idx + 3] = 0;

              if (x > 0) stack.push([x - 1, y]);
              if (x < width - 1) stack.push([x + 1, y]);
              if (y > 0) stack.push([x, y - 1]);
              if (y < height - 1) stack.push([x, y + 1]);
            }
          }
      }

      ctx.putImageData(imageData, 0, 0);
      
      const finalCanvas = trimAndResizeCanvas(canvas);
      
      resolve(finalCanvas.toDataURL('image/png'));
    };
    img.onerror = (e) => {
        console.error("Failed to process image for transparency", e);
        resolve(base64Data);
    };
    img.src = base64Data;
  });
}

export function addStickerContour(base64Data: string): Promise<string> {
    return new Promise((resolve) => {
        if (typeof window === 'undefined') {
            resolve(base64Data);
            return;
        }

        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const width = img.width;
            const height = img.height;
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                resolve(base64Data);
                return;
            }

            const scale = 0.90; 
            const scaledW = width * scale;
            const scaledH = height * scale;
            const offsetX = (width - scaledW) / 2;
            const offsetY = (height - scaledH) / 2;

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext('2d');
            if(!tempCtx) { resolve(base64Data); return; }

            tempCtx.drawImage(img, offsetX, offsetY, scaledW, scaledH);
            
            tempCtx.globalCompositeOperation = 'source-in';
            tempCtx.fillStyle = '#ffffff';
            tempCtx.fillRect(0, 0, width, height);

            const borderThickness = width * 0.025; 
            
            ctx.shadowColor = 'white';
            ctx.shadowBlur = borderThickness; 

            const steps = 36; 
            for (let i = 0; i < steps; i++) {
                const angle = (i * 2 * Math.PI) / steps;
                const sx = Math.cos(angle) * borderThickness;
                const sy = Math.sin(angle) * borderThickness;
                ctx.drawImage(tempCanvas, sx, sy);
            }
            
            ctx.shadowBlur = 0;
            ctx.drawImage(tempCanvas, 0, 0);

            ctx.globalCompositeOperation = 'source-over';
            ctx.drawImage(img, offsetX, offsetY, scaledW, scaledH);
            
            const finalCanvas = trimAndResizeCanvas(canvas);

            resolve(finalCanvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(base64Data);
        img.src = base64Data;
    });
}

export async function generateStickerIcons(prompt: string): Promise<string[]> {
  const res = await fetch('/api/generate-icons', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await getAppCheckHeader()), ...(await getAuthHeader()) },
    body: JSON.stringify({ prompt }),
  });
  const data = await res.json();
  if (!res.ok) {
    const msg = (data.error || '').toLowerCase();
    if (res.status === 429 || msg.includes('kredit') || msg.includes('limit')) {
      throw new Error("Aktuální limit generování vyčerpán, zkuste to prosím později.");
    }
    if (msg.includes('safety') || msg.includes('blocked')) {
      throw new Error("Obrázek se nepodařilo vygenerovat kvůli bezpečnostním filtrům. Zkuste jiný popis.");
    }
    throw new Error(data.error || "Generování selhalo.");
  }
  if (!data.images?.length) throw new Error("AI nevrátila žádné obrázky.");
  return data.images;
}
